import { Box, type SxProps, Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import type { DateTime } from "luxon";
import type React from "react";
import { type CSSProperties, type FC, Fragment } from "react";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useJobColors } from "../../../hooks/useJobColors";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useRoleColors } from "../../../hooks/useRoleColors";
import { NEUTRAL, SURFACE } from "../../../theme/tokens";
import { parseTimeString } from "../../../utils/dateUtils";
import { NurseFullRole, type NurseInShift } from "../../../utils/models";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { emptyLocationKey, locationAndRolesWidth, locationTypographySx, minShiftWidth, rowGap, rowPadding, shiftGaps } from "../utils";
import { blockWidthPct, hidesActionsUntilHover, normalizeTimes, slotLayout } from "./blockLayout";
import { DraggableShiftBlock } from "./DraggableShiftBlock";
import { DroppableGapBlock } from "./DroppableGapBlock";
import { InnerBlock } from "./InnerBlock";

type SpecializedInnerRowDnD = { nurseInShift?: NurseInShift; gapIdx?: never } | { nurseInShift?: never; gapIdx?: string };
type SpecializedInnerRowOptimisticChange = { isBeingMoved?: boolean; isOptimisticLoading?: boolean };
export type RowBadge = {
	badgeSx?: SxProps;
	text: string;
	onBadgeClick?: (e: React.MouseEvent<HTMLElement>) => void;
};
export type RowElement = {
	key: string | number;
	start: DateTime;
	end: DateTime;
	realStartEndTimes?: { realStart: DateTime; realEnd: DateTime };
	normalizedStart?: DateTime;
	normalizedEnd?: DateTime;
	mainShortText: React.ReactNode;
	disabledHideTextOnHover?: boolean;
	shortTextTrailingNode?: (isHovered: boolean, hideActionsWhenNotHovered: boolean) => React.ReactNode;
	tooltipFullText?: string;
	accessibleLabel?: string;
	accessibleDescription?: string;
	bgColor: CSSProperties["backgroundColor"];
	backgroundImage?: CSSProperties["backgroundImage"];
	color?: CSSProperties["color"];
	fontWeight?: CSSProperties["fontWeight"];
	onRowClick?: (e: React.MouseEvent<HTMLElement>) => void;
	badge?: RowBadge;
	leadingBadge?: RowBadge;
	/** DOM id on the block, so another element can `scrollIntoView` to it and pulse it (e.g. a posted-shift block). */
	domId?: string;
} & SpecializedInnerRowDnD &
	SpecializedInnerRowOptimisticChange;

type DailyRowSlot = {
	start: DateTime;
	end: DateTime;
	elements: Array<RowElement>;
};

const parentVariants = {
	hidden: {},
	show: { transition: { staggerChildren: 0.12, delayChildren: 0.07 } },
};

const childVariants = {
	hidden: { x: -20, opacity: 0 },
	show: { x: 0, opacity: 1 },
};

/**
 * Calculates the spacer width percentage needed before an element to account for time gaps
 * between consecutive elements in the flex layout.
 */
const getSpacerWidthPct = (
	elements: RowElement[],
	idx: number,
	normStart: DateTime,
	slotStart: DateTime,
	dayEnd: DateTime,
	containerWidthPct: number,
): number => {
	let refEnd = slotStart;
	if (idx !== 0) {
		const prev = normalizeTimes(elements[idx - 1]).end;
		refEnd = prev > dayEnd ? dayEnd : prev;
	}

	const gapMinutes = normStart.diff(refEnd, "minutes").as("minutes");
	return gapMinutes > 0 ? (gapMinutes / 60 / 24) * 100 * (100 / containerWidthPct) : 0;
};

type Props = {
	rowKey: string | number;
	date: DateTime;
	roles: (NurseFullRole | "all")[];
	name: string;
	rows: Array<DailyRowSlot>;
	disableBottomBorder?: boolean;
	rowBgColor?: CSSProperties["backgroundColor"];
	isMissingConfig: boolean;

	// Drag & Drop props
	locationKey?: string;
	/** Job linked to this row's location. When JobManagement is enabled, used to render a job-colored bar instead of role bars. */
	locationJobId?: string;
};

export const DailyRow: FC<Props> = ({
	name,
	rowKey,
	rows,
	date,
	roles,
	disableBottomBorder,
	rowBgColor = SURFACE,
	locationKey,
	isMissingConfig,
	locationJobId,
}) => {
	const roleColor = useRoleColors();
	const { chosenDepartment } = useHospitalData();
	const jobColor = useJobColors();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const linkedJob = jobManagementEnabled && locationJobId ? chosenDepartment.jobs?.find((j) => j.id === locationJobId) : undefined;
	const dayStartHour = parseTimeString(chosenDepartment.dayStart).hour;
	return (
		<Box
			key={name + rowKey + date.toISODate()}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "start",
				width: "100%",
				bgcolor: rowBgColor,
				gap: shiftGaps,
				px: rowPadding,
				paddingTop: rowGap,
				borderBottom: disableBottomBorder ? undefined : `1px solid ${NEUTRAL[150]}`,
			}}
		>
			<Box
				sx={[
					{
						display: "flex",
						alignItems: "center",
						justifyContent: "start",
						gap: rowGap * 4,
						minWidth: locationAndRolesWidth,
						height: "100%",
					},
					...(Array.isArray(locationTypographySx) ? locationTypographySx : [locationTypographySx]),
				]}
			>
				{rowKey !== emptyLocationKey && (
					<Box
						sx={{
							minWidth: "30px",
							height: "100%",
							display: "flex",
							gap: rowGap,
							paddingBottom: rowGap,
						}}
					>
						{jobManagementEnabled && linkedJob !== undefined ? (
							<Tooltip title={linkedJob.displayName}>
								<Box
									sx={{
										bgcolor: jobColor(linkedJob.id).dot,
										width: "6px",
										height: "100%",
										minHeight: "18px",
									}}
								/>
							</Tooltip>
						) : (
							(roles.includes("all") ? chosenDepartment.roles.map((role) => NurseFullRole[role]) : roles.filter((role) => role !== "all"))
								.sort()
								.map((role) => (
									<Tooltip key={role} title={role}>
										<Box
											sx={{
												bgcolor: roleColor(role).dot,
												width: "6px",
												height: "100%",
												minHeight: "18px",
											}}
										/>
									</Tooltip>
								))
						)}
					</Box>
				)}
				{isMissingConfig ? (
					<Tooltip title={"This location is outside of the scheduling configuration, please check your Recurring Shifts locations list."}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "start",
								gap: 0.5,
								color: "error.main",
							}}
						>
							{name}*
						</Box>
					</Tooltip>
				) : (
					name
				)}
			</Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "start",
					justifyContent: "start",
					width: `calc(100% - ${locationAndRolesWidth})`,
					gap: 1,
				}}
			>
				{rows.map((rowSlot) => {
					const slotKey = `slot_${rowKey}__${rowSlot.start.valueOf()}__${rowSlot.end.valueOf()}_${rowSlot.elements.at(0)?.key}`;
					// Handle overnight times
					const layout = slotLayout(rowSlot.start, date, dayStartHour);
					const { dayEnd, effectiveSlotStart, offsetPct, containerWidthPct } = layout;
					return (
						<Box
							key={slotKey}
							sx={{
								display: "flex",
								alignItems: "start",
								justifyContent: "start",
								ml: `${offsetPct}%`,
								width: `calc(100% - ${offsetPct}%)`,
							}}
						>
							{rowSlot.elements.map((innerElement, idx) => {
								const { start: normStart, end: normEnd } = normalizeTimes(innerElement);
								const specializedRowInnerKey = `${slotKey}__${innerElement.key}__${innerElement.start.valueOf()}__${innerElement.end.valueOf()}`;
								const currentElementWidthPercentage = blockWidthPct(normStart, normEnd, layout);
								const spacerWidthPct = getSpacerWidthPct(rowSlot.elements, idx, normStart, effectiveSlotStart, dayEnd, containerWidthPct);
								const displayStart = innerElement.realStartEndTimes?.realStart ?? innerElement.start;
								const displayEnd = innerElement.realStartEndTimes?.realEnd ?? innerElement.end;
								const shouldHideTextOnHover = hidesActionsUntilHover(currentElementWidthPercentage, innerElement.disabledHideTextOnHover === true);
								return (
									<Fragment key={`${innerElement.key}_fragment`}>
										{spacerWidthPct > 0 && (
											<Box
												key={`spacer_${innerElement.key}`}
												sx={{
													width: `${spacerWidthPct}%`,
													flexShrink: 0,
												}}
											/>
										)}
										<Box
											key={`${innerElement.key}_container`}
											component={motion.div}
											initial="hidden"
											animate="show"
											variants={parentVariants}
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "start",
												width: `${currentElementWidthPercentage}%`,
												minWidth: minShiftWidth,
												pr: rowGap,
											}}
										>
											<InHouseTooltip
												key={specializedRowInnerKey}
												title={`${displayStart.toFormat("h:mma")} - ${displayEnd.toFormat("h:mma")}${innerElement.tooltipFullText ? `\n${innerElement.tooltipFullText}` : ""}`}
												slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
											>
												<Box
													key={`motion-${specializedRowInnerKey}`}
													component={motion.div}
													variants={childVariants}
													transition={{
														type: "spring",
														stiffness: 200,
														damping: 30,
													}}
													sx={{
														width: "100%",
													}}
												>
													{innerElement.nurseInShift && locationKey && !innerElement.isBeingMoved && !innerElement.isOptimisticLoading ? (
														<DraggableShiftBlock
															innerRow={innerElement}
															nurseInShift={innerElement.nurseInShift}
															locationKey={locationKey}
															hideMainTextOnHover={shouldHideTextOnHover}
														/>
													) : innerElement.gapIdx !== undefined && locationKey ? (
														<DroppableGapBlock
															innerRow={innerElement}
															locationKey={locationKey}
															locationName={name}
															gapStart={innerElement.start}
															gapEnd={innerElement.end}
															roles={roles}
															gapIdx={innerElement.gapIdx}
															hideMainTextOnHover={shouldHideTextOnHover}
														/>
													) : (
														<InnerBlock innerRow={innerElement} hideMainTextOnHover={shouldHideTextOnHover} />
													)}
												</Box>
											</InHouseTooltip>
										</Box>
									</Fragment>
								);
							})}
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};
